import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Upload, X, FileText, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { transcribeVideo } from '../lib/api';
import { TranscriptionRequest } from '../lib/types';
export function BatchProcessing() {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [urls, setUrls] = useState<string[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => file.type === 'text/plain');
    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles]);
    }
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files).filter(file => file.type === 'text/plain');
      setFiles(prev => [...prev, ...selectedFiles]);
    }
  };
  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const addUrl = () => {
    if (urlInput && !urls.includes(urlInput)) {
      setUrls([...urls, urlInput]);
      setUrlInput('');
    }
  };
  const removeUrl = (index: number) => {
    setUrls(urls.filter((_, i) => i !== index));
  };
  const startProcessing = async () => {
    setProcessing(true);
    setProgress(0);
    setProcessedCount(0);
    setErrors({});
    const fileUrls: string[] = [];
    for (const file of files) {
      try {
        const text = await file.text();
        const fileLines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0 && !line.startsWith('#'));
        fileUrls.push(...fileLines);
      } catch (err) {
        console.error(`Error reading file ${file.name}:`, err);
        setErrors(prev => ({
          ...prev,
          [file.name]: `Failed to read file: ${err instanceof Error ? err.message : 'Unknown error'}`
        }));
      }
    }
    const allUrls = [...new Set([...fileUrls, ...urls])];
    const totalCount = allUrls.length;
    if (totalCount === 0) {
      setErrors(prev => ({
        ...prev,
        general: 'No valid URLs found to process'
      }));
      setProcessing(false);
      return;
    }
    for (let i = 0; i < allUrls.length; i++) {
      const url = allUrls[i];
      try {
        const request: TranscriptionRequest = {
          url,
          extract_audio: true,
          extract_metadata: true
        };
        await transcribeVideo(request);
        setProcessedCount(prev => prev + 1);
        setProgress(Math.round((i + 1) / totalCount * 100));
      } catch (err) {
        console.error(`Error processing URL ${url}:`, err);
        setErrors(prev => ({
          ...prev,
          [url]: `Failed to process: ${err instanceof Error ? err.message : 'Unknown error'}`
        }));
      }
    }
    setProcessing(false);
  };
  const clearAll = () => {
    setFiles([]);
    setUrls([]);
    setProgress(0);
    setProcessedCount(0);
    setErrors({});
  };
  return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-1618">
      <div className="mb-6 md:mb-8" data-id="element-1619">
        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold" data-id="element-1620">
          Batch Processing
        </h1>
        <p className="text-sm md:text-base text-gray-500 mt-2" data-id="element-1621">
          Process multiple TikTok videos at once by uploading a file with URLs
          or entering them manually
        </p>
      </div>
      {Object.keys(errors).includes('general') && <div className="mb-4 p-3 md:p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive flex items-start" data-id="element-1622">
          <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-1623" />
          <div data-id="element-1624">
            <p className="font-medium" data-id="element-1625">Error</p>
            <p className="text-sm" data-id="element-1626">{errors.general}</p>
          </div>
        </div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8" data-id="element-1627">
        <div data-id="element-1628">
          <Card className="mb-4 md:mb-6" data-id="element-1629">
            <CardContent className="p-4 md:p-6" data-id="element-1630">
              <h2 className="text-lg md:text-xl font-semibold mb-4" data-id="element-1631">
                Upload URL Files
              </h2>
              <div className={`border-2 border-dashed rounded-lg p-4 md:p-8 text-center ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-1632">
                <Upload className="mx-auto h-8 md:h-10 lg:h-12 w-8 md:w-10 lg:w-12 text-gray-400 mb-3 md:mb-4" data-id="element-1633" />
                <p className="mb-2 text-xs md:text-sm text-gray-500" data-id="element-1634">
                  <span className="font-semibold" data-id="element-1635">Click to upload</span> or drag
                  and drop
                </p>
                <p className="text-xs text-gray-500 mb-4" data-id="element-1636">
                  TXT files with one TikTok URL per line
                </p>
                <input type="file" accept=".txt" multiple className="hidden" id="file-upload" onChange={handleFileInput} data-id="element-1637" />
                <label htmlFor="file-upload" data-id="element-1638">
                  <Button variant="outline" className="pointer-events-none text-sm" data-id="element-1639">
                    Select Files
                  </Button>
                </label>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1640">
            <CardContent className="p-4 md:p-6" data-id="element-1641">
              <h2 className="text-lg md:text-xl font-semibold mb-4" data-id="element-1642">
                Enter URLs Manually
              </h2>
              <div className="flex flex-col sm:flex-row gap-2 mb-4" data-id="element-1643">
                <Input value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="Paste TikTok URL" className="flex-1" data-id="element-1644" />
                <Button onClick={addUrl} disabled={!urlInput} className="w-full sm:w-auto" data-id="element-1645">
                  Add
                </Button>
              </div>
              {urls.length > 0 && <div className="mt-4 space-y-2 max-h-[200px] overflow-y-auto" data-id="element-1646">
                  {urls.map((url, index) => <div key={index} className={`flex items-center justify-between p-2 rounded ${errors[url] ? 'bg-destructive/10 border border-destructive/20' : 'bg-secondary/30'}`} data-id="element-1647">
                      <span className="text-xs md:text-sm truncate flex-1" data-id="element-1648">
                        {url}
                        {errors[url] && <div className="text-xs text-destructive mt-1" data-id="element-1649">
                            {errors[url]}
                          </div>}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => removeUrl(index)} className="text-muted-foreground hover:text-destructive ml-2 p-1 h-auto" data-id="element-1650">
                        <X size={16} data-id="element-1651" />
                      </Button>
                    </div>)}
                </div>}
            </CardContent>
          </Card>
        </div>
        <div data-id="element-1652">
          <Card className="mb-4 md:mb-6" data-id="element-1653">
            <CardContent className="p-4 md:p-6" data-id="element-1654">
              <div className="flex justify-between items-center mb-4" data-id="element-1655">
                <h2 className="text-lg md:text-xl font-semibold" data-id="element-1656">
                  Files to Process
                </h2>
                <span className="text-xs md:text-sm text-gray-500" data-id="element-1657">
                  {files.length} file{files.length !== 1 ? 's' : ''}
                </span>
              </div>
              {files.length > 0 ? <div className="space-y-3 max-h-[300px] overflow-y-auto" data-id="element-1658">
                  {files.map((file, index) => <div key={index} className={`flex items-center justify-between p-2 md:p-3 rounded ${errors[file.name] ? 'bg-destructive/10 border border-destructive/20' : 'bg-secondary/30'}`} data-id="element-1659">
                      <div className="flex items-center space-x-2 md:space-x-3" data-id="element-1660">
                        <FileText size={16} className="text-primary hidden xs:block" data-id="element-1661" />
                        <div data-id="element-1662">
                          <p className="font-medium text-xs md:text-sm" data-id="element-1663">
                            {file.name}
                          </p>
                          <p className="text-xs text-muted-foreground" data-id="element-1664">
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                          {errors[file.name] && <p className="text-xs text-destructive mt-1" data-id="element-1665">
                              {errors[file.name]}
                            </p>}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(index)} className="text-muted-foreground hover:text-destructive ml-2 p-1 h-auto" data-id="element-1666">
                        <X size={16} data-id="element-1667" />
                      </Button>
                    </div>)}
                </div> : <div className="text-center py-6 md:py-8 text-gray-500" data-id="element-1668">
                  <AlertCircle className="mx-auto h-8 md:h-10 w-8 md:w-10 mb-2 text-gray-400" data-id="element-1669" />
                  <p className="text-sm" data-id="element-1670">No files selected</p>
                </div>}
            </CardContent>
          </Card>
          <Card data-id="element-1671">
            <CardContent className="p-4 md:p-6" data-id="element-1672">
              <h2 className="text-lg md:text-xl font-semibold mb-4" data-id="element-1673">
                Process Batch
              </h2>
              {processing && <div className="mb-4" data-id="element-1674">
                  <div className="flex justify-between items-center mb-1" data-id="element-1675">
                    <span className="text-xs md:text-sm" data-id="element-1676">Processing...</span>
                    <span className="text-xs md:text-sm" data-id="element-1677">
                      {processedCount}/{files.length + urls.length} files
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1678">
                    <div className="h-full bg-blue-600 rounded-full animate-pulse w-full" data-id="element-1679"></div>
                  </div>
                </div>}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-1680">
                <div data-id="element-1681">
                  {processing ? <span className="flex items-center text-xs md:text-sm text-blue-600" data-id="element-1682">
                      <RefreshCw size={14} className="mr-1 animate-spin" data-id="element-1683" />
                      Processing... ({processedCount}/
                      {files.length + urls.length})
                    </span> : progress === 100 ? <span className="flex items-center text-xs md:text-sm text-green-600" data-id="element-1684">
                      <CheckCircle size={14} className="mr-1" data-id="element-1685" />
                      Complete ({processedCount}/{files.length + urls.length})
                    </span> : <span className="text-xs md:text-sm text-gray-500" data-id="element-1686">
                      {files.length + urls.length} items ready
                    </span>}
                </div>
                <div className="flex gap-2 w-full sm:w-auto" data-id="element-1687">
                  <Button variant="outline" disabled={processing || files.length === 0 && urls.length === 0} onClick={clearAll} className="flex-1 sm:flex-initial text-xs md:text-sm py-2 h-auto" data-id="element-1688">
                    Clear All
                  </Button>
                  <Button disabled={processing || files.length === 0 && urls.length === 0} onClick={startProcessing} className="flex-1 sm:flex-initial text-xs md:text-sm py-2 h-auto" data-id="element-1689">
                    {processing ? <span className="flex items-center" data-id="element-1690">
                        <RefreshCw size={14} className="mr-2 animate-spin" data-id="element-1691" />
                        Processing...
                      </span> : 'Start Processing'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}